import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import { Users, CreditCard, BookOpen, Award, TrendingUp, IndianRupee, Clock, AlertCircle } from "lucide-react";

const AdminOverview = () => {
  const [stats, setStats] = useState({ students: 0, payments: 0, revenue: 0, certificates: 0, courses: 0 });
  const [loading, setLoading] = useState(true);
  const [recentStudents, setRecentStudents] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [studentsSnap, paymentsSnap, certsSnap, coursesSnap] = await Promise.all([
          getDocs(collection(db, "students")),
          getDocs(collection(db, "payments")),
          getDocs(collection(db, "certificates")),
          getDocs(collection(db, "courses")),
        ]);
        const studs = studentsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const pays = paymentsSnap.docs.map((d) => d.data());
        const revenue = pays.reduce((sum, p) => sum + (p.paymentStatus === "success" ? Number(p.amount) || 0 : 0), 0);
        const studentIds = new Set(studs.map((s) => s.studentId || s.id).filter(Boolean));
        const issuedStudentIds = new Set();
        certsSnap.docs.forEach((docSnap) => {
          const cert = docSnap.data();
          if (cert.manualPdfUploadedAt && studentIds.has(cert.studentId)) {
            issuedStudentIds.add(cert.studentId);
          }
        });
        const certificateCount = Math.min(issuedStudentIds.size, studs.length);
        setStats({
          students: studs.length,
          payments: pays.filter((p) => p.paymentStatus === "success").length,
          revenue,
          certificates: certificateCount,
          courses: coursesSnap.size,
        });
        setRecentStudents(studs.slice(0, 5));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetch();
  }, []);

  const cards = [
    { label: "Students", value: stats.students, icon: Users, color: "blue" },
    { label: "Total Revenue", value: `₹${stats.revenue.toLocaleString("en-IN")}`, icon: IndianRupee, color: "emerald" },
    { label: "Certificates Issued", value: stats.certificates, icon: Award, color: "violet" },
    { label: "Successful Payments", value: stats.payments, icon: CreditCard, color: "amber" },
    { label: "Active Courses", value: stats.courses, icon: BookOpen, color: "sky" },
  ];

  const colorMap = {
    blue: "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
    sky: "bg-sky-50 text-sky-700",
  };

  return (
    <div data-testid="admin-overview">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-2">Admin Console</div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">Overview</h1>
        <p className="mt-1 text-slate-600">Platform-wide statistics and recent activity.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-5">
            <div className={`w-10 h-10 rounded-lg ${colorMap[c.color]} flex items-center justify-center mb-3`}>
              <c.icon className="w-5 h-5" />
            </div>
            <div className="font-heading text-2xl font-bold text-slate-900">{loading ? "..." : c.value}</div>
            <div className="text-xs text-slate-500 mt-1">{c.label}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-slate-900">Recent Registrations</h2>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading...</div>
        ) : recentStudents.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="no-recent-students">No students yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-6 py-3 font-semibold">Student ID</th>
                <th className="text-left px-6 py-3 font-semibold">Name</th>
                <th className="text-left px-6 py-3 font-semibold">Email</th>
                <th className="text-left px-6 py-3 font-semibold">College</th>
                <th className="text-left px-6 py-3 font-semibold">Program</th>
                <th className="text-left px-6 py-3 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {recentStudents.map((s) => (
                <tr key={s.id} className="border-t border-slate-100">
                  <td className="px-6 py-3 font-mono text-xs">{s.studentId}</td>
                  <td className="px-6 py-3 font-medium text-slate-900">{s.fullName}</td>
                  <td className="px-6 py-3 text-slate-700">{s.email}</td>
                  <td className="px-6 py-3 text-slate-700">{s.college}</td>
                  <td className="px-6 py-3 text-slate-700">{s.selectedInternship}</td>
                  <td className="px-6 py-3 font-semibold">₹{s.amountPaid}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminOverview;
